import { useEffect, useRef } from 'react';
import {
    listVoiceOutbox,
    recordAttemptFailure,
    type VoiceOutboxRecord,
} from '../utils/voiceOutbox';
import { uploadVoiceOutboxRecord } from './useVoiceChatSession';
import { useMessagingStore } from '../../../stores/messagingStore';
import { useTokens } from '../../../contexts/TokenContext';
import type { Message } from '../../../types/messaging';

const TICK_MS = 1_000;

/**
 * useVoiceOutboxScheduler — drains the IndexedDB voice outbox.
 *
 * Two responsibilities:
 *   1. On mount: rehydrate any pending records from a previous session
 *      into the message store as ghost bubbles (so the user sees their
 *      "in-flight" recordings instead of an empty thread + a delayed
 *      surprise message).
 *   2. Every TICK_MS: scan for records whose nextAttemptAt has elapsed
 *      and try uploading them. Failures bump the attempt counter and
 *      reschedule via exponential backoff; once attempts >= MAX_ATTEMPTS
 *      the record is marked `dead` and stays put for manual retry/discard.
 *
 * The scheduler runs while VoiceChatInput is mounted. That's intentional:
 * voice mode is the only path that produces outbox entries, so there's
 * no value running it elsewhere — and a global scheduler would have to
 * decide which conversation to drop ghost bubbles into.
 *
 * Multi-tab note: we don't yet take a BroadcastChannel lock, so two tabs
 * with voice mode open could double-upload a record. In practice this is
 * OK — the server creates one message per upload; the duplicate would
 * appear as two bot replies, which is mildly annoying but not destructive.
 * Add a lock here if telemetry shows that pattern in the wild.
 */
export function useVoiceOutboxScheduler(currentConversationId: string, currentUserId: string): void {
    const { refreshStats: refreshTokenStats } = useTokens();
    const inFlightRef = useRef<Set<string>>(new Set());

    useEffect(() => {
        let cancelled = false;
        let timer: ReturnType<typeof setInterval> | null = null;

        const ensureGhostBubble = (record: VoiceOutboxRecord) => {
            const store = useMessagingStore.getState();
            // Only show ghost bubbles for the currently-open conversation —
            // putting them in the wrong thread would confuse the user.
            if (record.conversationId !== currentConversationId) return;
            const exists = store.messages.some((m) => m.id === record.tempId);
            if (exists) return;
            const ghost: Message = {
                id: record.tempId,
                conversationId: record.conversationId,
                senderId: currentUserId,
                content: '',
                isRead: true,
                createdAt: new Date(record.createdAt).toISOString(),
                audioUrl: null,
                audioDurationMs: null,
                metadata: {
                    isVoiceMode: true,
                    isTranscribing: false,
                    queueState: record.dead ? 'failed' : 'retrying',
                    attempts: record.attempts,
                    lastError: record.lastError,
                },
            };
            store.addLocalMessage(ghost);
        };

        const tick = async () => {
            if (cancelled) return;
            let records: VoiceOutboxRecord[];
            try {
                records = await listVoiceOutbox();
            } catch {
                return;
            }
            const now = Date.now();
            for (const record of records) {
                ensureGhostBubble(record);

                if (record.dead) continue;
                if (record.nextAttemptAt > now) continue;
                if (inFlightRef.current.has(record.tempId)) continue;

                inFlightRef.current.add(record.tempId);
                try {
                    await uploadVoiceOutboxRecord(record, refreshTokenStats);
                } catch (err) {
                    const errMsg = (err as Error).message;
                    const updated = await recordAttemptFailure(record.tempId, errMsg).catch(() => null);
                    useMessagingStore.getState().updateLocalMessage(record.tempId, {
                        metadata: {
                            isVoiceMode: true,
                            isTranscribing: false,
                            queueState: updated?.dead ? 'failed' : 'retrying',
                            attempts: updated?.attempts ?? record.attempts + 1,
                            lastError: errMsg,
                        },
                    });
                } finally {
                    inFlightRef.current.delete(record.tempId);
                }
            }
        };

        // Sweep once immediately so ghost bubbles appear before the user
        // notices the empty space, then poll on TICK_MS.
        void tick();
        timer = setInterval(() => void tick(), TICK_MS);

        return () => {
            cancelled = true;
            if (timer) clearInterval(timer);
        };
    }, [currentConversationId, currentUserId, refreshTokenStats]);
}
